import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { switchMap, map, catchError, mergeMap } from 'rxjs/operators';

import * as CommonIdPlaylistActions from '../../actions/common-id/common-id-playlist.actions';
import { CommonIdPlaylistService } from '../../services/common-id/common-id-playlist.service';
import { FirebaseStorageError } from '../../errors/firebase-storage-error';
import { GeneralError } from '../../errors/general-error';
import { dispatchAppError } from '../../actions/core.actions';
import { FirestoreError } from '../../errors/firestore-error';

@Injectable()
export class CommonIdPlaylistEffects {
  private LOG_SOURCE = this.constructor.name;

  findPlaylists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonIdPlaylistActions.findPlaylists),
      switchMap(() =>
        this.playlistService.findPlaylists().pipe(
          map(playlists => CommonIdPlaylistActions.findPlaylistsSuccess({ playlists })),
          catchError(e =>
            of(CommonIdPlaylistActions.findPlaylistsFailure(), dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) }))
          )
        )
      )
    )
  );

  findPlaylistProblems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonIdPlaylistActions.findPlaylistProblems),
      switchMap(action =>
        this.playlistService.findPlaylistProblems(action.subjectId, action.playlistId).pipe(
          map(playlistProblems => CommonIdPlaylistActions.findPlaylistProblemsSuccess({ subjectId: action.subjectId, playlistProblems })),
          catchError(e =>
            of(
              CommonIdPlaylistActions.findPlaylistProblemsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) })
            )
          )
        )
      )
    )
  );

  findPlaylistProblemIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonIdPlaylistActions.findPlaylistProblemIds),
      switchMap(action =>
        this.playlistService.findPlayListProblemIds(action.subjectId, action.playlistId).pipe(
          map(playlistProblemIds => CommonIdPlaylistActions.findPlaylistProblemIdsSuccess({ playlistProblemIds })),
          catchError(e =>
            of(
              CommonIdPlaylistActions.findPlaylistProblemIdsFailure(),
              dispatchAppError({ source: this.LOG_SOURCE, error: FirestoreError.from(e) })
            )
          )
        )
      )
    )
  );

  resolvePdfObjectUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CommonIdPlaylistActions.resolvePdfObjectUrl),
      mergeMap(action =>
        this.playlistService.getPdfObjectUrl(action.pdfPath).pipe(
          map(objectUrl => CommonIdPlaylistActions.resolvePdfObjectUrlSuccess({ pdfPath: action.pdfPath, objectUrl })),
          catchError(e => {
            const error = e instanceof FirebaseStorageError ? e : GeneralError.unknown(e);
            return of(
              CommonIdPlaylistActions.resolvePdfObjectUrlFailure({ pdfPath: action.pdfPath }),
              dispatchAppError({ source: this.LOG_SOURCE, error })
            );
          })
        )
      )
    )
  );

  constructor(private actions$: Actions, private playlistService: CommonIdPlaylistService) {}
}
